import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useMediaQuery,
  useToast,
} from '@chakra-ui/react';
import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import type { Cart } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  apiInstance,
  buyerCaptureException,
  buyerCurrencyFormatter,
  getBannerReferer,
  getItemCardTrackingParams,
  isEven,
  trackEvent,
} from '@diamond/shared/utils';
import { TRACKING_EVENTS } from '@diamond/sol-buyer/constant';
import {
  hasCountVouchers,
  selectCartItems,
  usePromoVoucherPreviewEnabled,
  useStockNumberInput,
  useVoucherDisplayEnabled,
} from '@diamond/sol-buyer/utils';
import {
  CART_TOTAL_QUERY_KEY,
  useCartStore,
  useCartTotalProductDetail,
  useProductVoucherEligibilityList,
} from '@diamond/sol-buyer-context';
import { type QueryKey, QueryClientContext } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { dinero, toDecimal } from 'dinero.js';
import { useContext, useEffect, useMemo, useState } from 'react';

import { QuantityControl } from '../../carts/QuantityControl';
import { CardProductProps } from '../card-product/card-product';
import { ModalNotification } from '../modal-notification';
import { buildProductPromoOffers } from '../promo-preview';
import { PromoSection } from './PromoSection';
import { VoucherSection } from './VoucherSection';

interface AddToCartPopupProps {
  isOpen: boolean;
  onClose: () => void;
  product?: CardProductProps['product'];
  productId: string;
  token: string;
  selectedShipToId: string;
  onSuccess?: () => void;
  section?: string;
  page?: string;
  defaultQuantity?: number;
  autoSelectProductId?: string;
  enableCartStoreSync?: boolean;
  shouldAutoSelectOnAdd?: boolean;
  onSyncStart?: () => void;
  onSyncComplete?: () => void;
}

export const AddToCartPopup: React.FC<AddToCartPopupProps> = ({
  isOpen,
  onClose,
  product,
  productId,
  token,
  selectedShipToId,
  onSuccess,
  section,
  page,
  defaultQuantity,
  autoSelectProductId,
  enableCartStoreSync = false,
  shouldAutoSelectOnAdd = false,
  onSyncStart,
  onSyncComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const toast = useToast();
  const queryClient = useContext(QueryClientContext);

  const invalidateQuery = (key: QueryKey) => {
    if (!queryClient) return Promise.resolve();
    return queryClient.invalidateQueries({ queryKey: key, exact: false, refetchType: 'active' });
  };

  const [magnifiedQty, setMagnifiedQty] = useState<number>(0);
  const [uiQuantity, setUiQuantity] = useState<number>(0);

  const orderQtyMultiple = Number(
    toDecimal(dinero({ amount: product?.order_quantity_multiple || 1, currency: STOCK_UI }))
  );
  const minOrderQty = Number(
    toDecimal(dinero({ amount: product?.minimum_order_quantity || 1, currency: STOCK_UI }))
  );
  const maxStock = Number(
    toDecimal(dinero({ amount: product?.stock ?? 0, currency: STOCK_UI }))
  );

  const maxStockGanjil = Math.floor(maxStock / orderQtyMultiple) * orderQtyMultiple;
  const maxStockGenap = Math.round(maxStock / orderQtyMultiple) * orderQtyMultiple;
  const maxAllowedQuantity =
    isEven(orderQtyMultiple) || orderQtyMultiple === 1 ? maxStockGanjil : maxStockGenap;
  const precision = orderQtyMultiple !== 1 ? 2 : 0;

  const normalizedDefaultQuantity = useMemo(() => {
    if (typeof defaultQuantity !== 'number' || Number.isNaN(defaultQuantity) || defaultQuantity <= 0) {
      return minOrderQty;
    }
    const maxTarget = maxAllowedQuantity > 0 ? maxAllowedQuantity : defaultQuantity;
    const clamped = Math.min(maxTarget, Math.max(defaultQuantity, minOrderQty));
    if (!orderQtyMultiple || orderQtyMultiple <= 0) return clamped;
    const multiples = Math.max(1, Math.round(clamped / orderQtyMultiple));
    return Math.min(Number((multiples * orderQtyMultiple).toFixed(precision)), maxTarget);
  }, [defaultQuantity, minOrderQty, maxAllowedQuantity, orderQtyMultiple, precision]);

  useEffect(() => {
    if (normalizedDefaultQuantity > 0 && magnifiedQty === 0) {
      setUiQuantity(normalizedDefaultQuantity);
    }
  }, [normalizedDefaultQuantity, magnifiedQty]);

  const { inputProps, incProps, decProps, hasFractionalStep } = useStockNumberInput({
    id: `input-qty-modal-${productId}`,
    name: `input-qty-modal-${productId}`,
    valueUi: uiQuantity || normalizedDefaultQuantity,
    stepUi: orderQtyMultiple,
    minUi: minOrderQty,
    maxUi: maxAllowedQuantity,
    stepMagnified: product?.order_quantity_multiple || 1,
    roundOnBlurOnly: true,
    isDisabled: minOrderQty > maxStock || product?.stock_status === 'OUT_OF_STOCK',
    onChangeMagnifiedImmediate: (magnified) => {
      setMagnifiedQty(magnified);
      setUiQuantity(magnified / 1000);
    },
    onChangeMagnified: (magnified) => {
      setMagnifiedQty(magnified);
      setUiQuantity(magnified / 1000);
    },
    onError: (errorMessage) => {
      showToast(toast, 'error', errorMessage, undefined, 'bottom', 3000);
    },
  });

  const effectiveMagnifiedQty = magnifiedQty || normalizedDefaultQuantity * 1000;
  const promoOffers = useMemo(() => buildProductPromoOffers(product), [product]);

  const isPromoVoucherPreviewEnabled = usePromoVoucherPreviewEnabled();
  const isVoucherDisplayEnabled = useVoucherDisplayEnabled();
  const { data: voucherData } = useProductVoucherEligibilityList({
    itemCode: product?.item_code,
    size: 5,
    enabled:
      isOpen &&
      isPromoVoucherPreviewEnabled &&
      isVoucherDisplayEnabled &&
      hasCountVouchers(product),
  });
  const voucherOffers = useMemo(
    () => voucherData?.pages.flatMap((p) => p.data) ?? [],
    [voucherData]
  );

  const handleUseQuantity = (targetQuantity: number) => {
    if (targetQuantity > maxAllowedQuantity * 1000) return;
    setMagnifiedQty(targetQuantity);
    setUiQuantity(targetQuantity / 1000);
  };

  const { data: cartTotalData, isFetching: isCartTotalFetching } =
    useCartTotalProductDetail(productId, effectiveMagnifiedQty, isOpen);

  const totalPrice = Math.round(cartTotalData?.finalPriceMagnify?.magnifiedValue || 0);

  const stockStatus = product?.stock_status ?? '';
  const available =
    maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'Available'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'Stok Terbatas'
      : 'Out of Stock';
  const statusColor =
    maxStock > 0 && stockStatus === 'AVAILABLE'
      ? 'azure'
      : maxStock > 0 && stockStatus === 'LIMITED_STOCK'
      ? 'orange'
      : 'red';

  const handleAddToCart = async () => {
    setIsLoading(true);
    setError('');
    try {
      const finalMagnifiedQty = magnifiedQty || normalizedDefaultQuantity * 1000;
      await apiInstance(token).post('/cart', {
        product_id: productId,
        warehouse_id: selectedShipToId,
        quantity: finalMagnifiedQty,
        note: null,
      });

      const bannerReferer = getBannerReferer();
      trackEvent(TRACKING_EVENTS.ADD_TO_CART, {
        currency: 'IDR',
        value: totalPrice,
        items: [
          {
            ...getItemCardTrackingParams(product, finalMagnifiedQty / 1000, section, page),
            index: 1,
            item_list_id: `${section} | ${product?.item_code}`,
            item_list_name: section,
          },
        ],
        section_name: section || '-',
        page_name: page || '-',
        ...(bannerReferer || {}),
      });

      try {
        if (enableCartStoreSync) {
          onSyncStart?.();

          await queryClient?.invalidateQueries({ queryKey: ['Cart'], exact: false, refetchType: 'active' });
          await queryClient?.refetchQueries({ queryKey: ['Cart'], exact: false, type: 'all' });

          const cartQueries = queryClient?.getQueriesData({ queryKey: ['Cart'], exact: false });

          let cartQueryData: Cart[] | undefined;
          if (cartQueries && cartQueries.length > 0) {
            for (const [, data] of cartQueries) {
              if (Array.isArray(data) && data.length > 0) {
                const foundItem = (data as Cart[]).find((item) => item.product.id === productId);
                if (foundItem) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
            if (!cartQueryData) {
              for (const [, data] of cartQueries) {
                if (Array.isArray(data) && data.length > 0) {
                  cartQueryData = data as Cart[];
                  break;
                }
              }
            }
          }

          let updatedCartItem: Cart | undefined;
          if (cartQueryData && Array.isArray(cartQueryData)) {
            updatedCartItem = cartQueryData.find((item) => item.product.id === productId);
          }

          if (updatedCartItem) {
            updateCartItemByProductId(productId, updatedCartItem);

            if (shouldAutoSelectOnAdd && autoSelectProductId === productId) {
              const isAlreadySelected = selectedCartItemIds.includes(updatedCartItem.id);
              if (!isAlreadySelected) {
                const preparedItems = selectCartItems([updatedCartItem.id], [updatedCartItem]);
                preparedItems.forEach((item) => addCartItem(item));
              }
            }

            const cartQueriesToUpdate = queryClient?.getQueriesData({ queryKey: ['Cart'], exact: false });
            if (cartQueriesToUpdate && cartQueriesToUpdate.length > 0) {
              cartQueriesToUpdate.forEach(([queryKey, data]) => {
                if (Array.isArray(data)) {
                  const currentData = data as Cart[];
                  const itemIndex = currentData.findIndex((item) => item.product.id === productId);
                  if (itemIndex !== -1) {
                    const updatedData = currentData.map((item, idx) =>
                      idx === itemIndex ? { ...updatedCartItem } : item
                    );
                    queryClient?.setQueryData(queryKey, updatedData);
                  }
                }
              });
            } else {
              console.warn('[AddToCartPopup] No Cart queries found to update');
            }
          } else {
            console.warn('[AddToCartPopup] updatedCartItem not found:', {
              productId,
              cartQueryDataLength: cartQueryData?.length,
            });
          }

          await invalidateQuery([CART_TOTAL_QUERY_KEY]);
          onSyncComplete?.();
        } else {
          await Promise.all([invalidateQuery(['Cart']), invalidateQuery([CART_TOTAL_QUERY_KEY])]);
          if (autoSelectProductId) queueAutoSelectProductId(autoSelectProductId);
        }

        setSuccess(true);
        if (onSuccess) onSuccess();
      } catch (invalidateError) {
        console.error('Cart update failed:', invalidateError);
        setError('Gagal memperbarui keranjang');
        buyerCaptureException({ message: 'Cart update failed' }, (scope, user) => {
          scope.setTag('cart-invalidation', `productId ${productId}`);
          scope.setUser({ ...user, additional: { selectedShipToId, section: section || '-', page: page || '-' } });
          scope.setLevel('error');
        });
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string; error?: string }>;
      const errorCode = axiosError?.response?.data?.error;
      const message = axiosError?.response?.data?.message || 'Gagal menambah ke keranjang';

      if (errorCode === 'DAILY_ORDER_LIMIT_EXCEEDED') {
        showToast(toast, 'error', message);
        handleClose();
      } else {
        setError(message);
      }

      buyerCaptureException(axiosError?.response?.data || { message }, (scope, user) => {
        scope.setTag('add-to-cart', `productId ${productId}`);
        scope.setUser({ ...user, additional: { selectedShipToId, section: section || '-', page: page || '-' } });
        scope.setLevel('error');
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError('');
    onClose();
  };

  const {
    setProductDetails,
    queueAutoSelectProductId,
    updateCartItemByProductId,
    addCartItem,
    selectedCartItemIds,
  } = useCartStore((state) => ({
    setProductDetails: state.setProductDetails,
    queueAutoSelectProductId: state.queueAutoSelectProductId,
    updateCartItemByProductId: state.updateCartItemByProductId,
    addCartItem: state.add,
    selectedCartItemIds: state.cartItems,
  }));

  useEffect(() => {
    if (product) {
      setProductDetails(productId, {
        basePrice: product?.prices?.[0]?.price || 0,
        discount_promos:
          product?.discount_promos?.map((promo) => ({
            id: promo.id,
            type: promo.is_discount ? 'PERCENTAGE' : 'FIXED_AMOUNT',
            value: promo.deduction_amount,
          })) || [],
      });
    }
  }, [product, productId, setProductDetails]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size={isMobile ? 'sm' : 'lg'}>
      <ModalOverlay />
      <ModalContent borderRadius="2xl">
        {!success && (
          <ModalHeader fontSize="xl" fontWeight="medium" pb={2}>
            Atur Jumlah Pesanan
          </ModalHeader>
        )}
        <ModalCloseButton size="lg" />
        <ModalBody>
          {success ? (
            <ModalNotification
              isOpen={success}
              onClose={handleClose}
              title={'Berhasil ditambahkan ke dalam keranjang!'}
              primaryLabel={'Lanjutkan Belanja'}
              onPrimary={handleClose}
              secondaryLabel={'Lihat Keranjang'}
              onSecondary={() => (window.location.href = '/cart')}
              size={isMobile ? 'xs' : 'md'}
            />
          ) : (
            <>
              <Flex align="flex-start" mb={4} gap={3}>
                <Box
                  boxSize={16}
                  bg="white"
                  rounded="sm"
                  overflow="hidden"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  border="1px solid"
                  borderColor="gray.200"
                  flexShrink={0}
                >
                  {product?.product_image_thumbnails?.[0] && (
                    <img
                      src={product.product_image_thumbnails[0]}
                      style={{ width: '100%', height: '100%', objectPosition: 'center', objectFit: 'contain' }}
                      alt={product?.desc || 'Product image'}
                    />
                  )}
                </Box>
                <Box flex={1} minW={0}>
                  <Text
                    fontWeight="medium"
                    fontSize="10px"
                    noOfLines={1}
                    textTransform="uppercase"
                    letterSpacing="wide"
                    mb={0.5}
                  >
                    {product?.brand_desc}
                  </Text>
                  <Text fontSize="xs" noOfLines={2} lineHeight="short" mb={0.5}>
                    {product?.desc}
                  </Text>
                  {product?.item_code && (
                    <Text fontSize="sm" color="gray.600" noOfLines={1} mb={2}>
                      {product.item_code}
                    </Text>
                  )}
                  <Flex align="center" justify="space-between" gap={2}>
                    <Text color={statusColor} fontSize="xs" flexShrink={0}>
                      {available}
                    </Text>
                    <Box flexShrink={0}>
                      <QuantityControl
                        variant="add-to-cart"
                        hasFractionalStep={hasFractionalStep}
                        inputProps={inputProps}
                        incProps={incProps}
                        decProps={decProps}
                        isLoading={isCartTotalFetching || isLoading}
                      />
                    </Box>
                  </Flex>
                </Box>
              </Flex>

              {isPromoVoucherPreviewEnabled && (
                <PromoSection
                  offers={promoOffers}
                  effectiveMagnifiedQty={effectiveMagnifiedQty}
                  maxAllowedQuantity={maxAllowedQuantity}
                  onUseQuantity={handleUseQuantity}
                />
              )}

              {isPromoVoucherPreviewEnabled && isVoucherDisplayEnabled && (
                <VoucherSection
                  vouchers={voucherOffers}
                  itemCode={product?.item_code}
                  effectiveMagnifiedQty={effectiveMagnifiedQty}
                  maxAllowedQuantity={maxAllowedQuantity}
                  onUseQuantity={handleUseQuantity}
                />
              )}

              <Box mb={2}>
                <Text fontSize="sm" fontWeight="semibold" color="#00003D">
                  Total
                </Text>
                {(cartTotalData?.savedPriceMagnify?.magnifiedValue ?? 0) > 0 && (
                  <Flex gap={2} mt={0.5} flexWrap="wrap">
                    <Text fontSize="xs" color="gray.500" textDecoration="line-through">
                      {buyerCurrencyFormatter(cartTotalData?.originalPriceMagnify?.magnifiedValue ?? 0)}
                    </Text>
                    <Text fontSize="xs" color="#FF383C">
                      Hemat {buyerCurrencyFormatter(cartTotalData?.savedPriceMagnify?.magnifiedValue ?? 0)}
                    </Text>
                  </Flex>
                )}
              </Box>
              {error && (
                <Text color="red.500" fontSize="sm" mb={2} textAlign="center">
                  {error}
                </Text>
              )}
            </>
          )}
        </ModalBody>
        {!success && (
          <ModalFooter gap={3} pt={0}>
            <Skeleton borderRadius="lg" isLoaded={!isCartTotalFetching} flex="1">
              <Box
                py={2.5}
                px={2}
                bgColor="#FAFAFA"
                rounded="lg"
                minH="40px"
                display="flex"
                alignItems="center"
              >
                <Flex align="baseline" gap={1}>
                  <Text color="#0000A3" fontWeight="medium" fontSize="sm">
                    Rp
                  </Text>
                  <Text color="#0000A3" fontWeight="medium" fontSize="xl">
                    {buyerCurrencyFormatter(
                      cartTotalData?.finalPriceMagnify?.magnifiedValue ?? 0
                    ).replace(/^Rp\s*/i, '')}
                  </Text>
                </Flex>
              </Box>
            </Skeleton>
            <Button
              w="96px"
              bgColor="#0000A3"
              color="white"
              onClick={handleAddToCart}
              isDisabled={
                isCartTotalFetching ||
                isLoading ||
                product?.stock_status === 'OUT_OF_STOCK' ||
                minOrderQty > maxStock
              }
              isLoading={isLoading || isCartTotalFetching}
              borderRadius="4px"
              height="40px"
              fontSize="xs"
              fontWeight="semibold"
              _hover={{ bgColor: '#000080' }}
            >
              + Keranjang
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
